import { capitalize } from '@buyerflo/core/helperFunctions/string'
import {
    NewPurchaserSchema,
    purchaserEnabledForUser,
    UpdateClientTabSchema,
    WishlistSchema,
} from '@buyerflo/core/schemas/purchaser'
import { VendorType } from '@buyerflo/core/schemas/vendor'
import { Card } from '@buyerflo/ui/components/Card'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePurchaserConfigApi, usePurchasersApi, useUserName, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import LocationAutocomplete from 'components/LocationAutocomplete'
import AgentInput from 'components/agentInput'
import Button from 'components/button'
import { SlotDatePicker } from 'components/datePicker'
import { AddRounded } from 'components/icons'
import Input, { SlotInput, SlotWrapper } from 'components/input'
import { Label } from 'components/label'
import Select, { Option } from 'components/select'
import { useShowSnack } from 'components/snackbar'
import UnsavedModal from 'components/unsavedModal'
import { addDays, format } from 'date-fns'
import { RenderPurchaserValues } from 'purchasers/additionalValues'
import { useEditMode } from 'purchasers/editMode'
import { CLIENT_DETAIL_MAX_WIDTH } from 'purchasers/helpers'
import EsignFormStepper from 'purchasers/esignFormStepper'
import { usePurchaserStatusOptions } from 'purchasers/helpers'
import { usePurchaserContext } from 'purchasers/purchaserProvider'
import BuyerList from 'purchasers/vendorList'
import { assoc, pick, prop } from 'ramda'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useBlocker, useNavigate } from 'react-router'
import { SizableText, View, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const CorePurchaserDetails = forwardRef(({}, ref: any) => {
    const trpc = useTRPC()
    const { purchaser, setPurchaser } = usePurchaserContext()
    const showSnack = useShowSnack()
    const navigate = useNavigate()
    const purchasersApi = usePurchasersApi(false)
    const purchaserConfigApi = usePurchaserConfigApi()
    const usersApi = useUsersApi(true)
    const userName = useUserName()
    const purchaserStatusOptions = usePurchaserStatusOptions(purchaser)
    const { editMode, setEditMode } = useEditMode()
    const createPurchaserMutation = useMutation(trpc.purchaser.createPurchaser.mutationOptions())
    const updateClientTabMutation = useMutation(trpc.purchaser.updateClientTab.mutationOptions())
    const queryClient = useQueryClient()

    // Saving the client writes a new audit log entry server-side, so mark the
    // cached audit log stale to force a refetch next time the tab is viewed.
    const invalidateAuditLog = () =>
        queryClient.invalidateQueries({
            queryKey: trpc.purchaser.getAuditLog.queryKey({ purchaserId: purchaser.purchaserId }),
        })

    const isNew = purchaser.purchaserId == undefined
    const { user } = useUserData()

    const [loading, setLoading] = useState(false)
    const [buyerType, setBuyerType] = useState<VendorType>(
        purchaser.buyers ? (purchaser.buyers[0]?.vendorType ?? 'individual') : 'individual',
    )

    const enabledForUser = purchaserEnabledForUser(purchaser, usersApi.data, user as any)

    const formEnabled =
        isNew ||
        user.permissions.includes('purchasers.fullControl') ||
        (purchaser.agentAppointmentForm?.esign?.formStatus == 'draft' && enabledForUser)

    const formDisabled = !formEnabled

    const formFullyDisabled =
        !isNew &&
        !user.permissions.includes('purchasers.fullControl') &&
        !(enabledForUser && user.permissions.includes('purchasers.edit'))

    const clientTabFields = [
        'agents',
        'authorityStartDate',
        'authorityPeriod',
        'commission',
        'commissionIncEx',
        'commissionType',
        'commitmentFee',
        'purchaserStatus',
        'additionalDetails',
    ] as const

    const defaultBuyer = {
        vendorType: 'individual' as const,
        firstName: '',
        lastName: '',
        email: '',
        mobileNumber: '',
    }

    const hookForm = useForm<UpdateClientTabSchema>({
        defaultValues: {
            authorityPeriod: 90,
            ...pick([...clientTabFields], purchaser),
            ...(isNew && !purchaser.authorityStartDate
                ? { authorityStartDate: format(new Date(), 'yyyy-MM-dd') }
                : {}),
            buyers: purchaser.buyers?.map(({ vendorType, ...v }) => ({
                ...v,
                vendorType: vendorType ?? 'individual',
            })) ?? [defaultBuyer],
        },
        resolver: zodResolver(isNew ? NewPurchaserSchema : UpdateClientTabSchema),
        shouldFocusError: true,
    })

    const [ocrLoading, setOcrLoading] = useState(false)

    const { control, handleSubmit, formState, getValues, setValue, setError, trigger, reset, watch } =
        hookForm

    useEffect(() => {
        const picked = pick([...clientTabFields, 'buyers'], purchaser) as any
        if (!picked.buyers?.length) picked.buyers = [defaultBuyer]
        reset(picked)
    }, [purchaser.purchaserId])

    // Persist form state to context on unmount when creating a new client (tab switch)
    useEffect(() => {
        return () => {
            if (isNew) {
                const values = hookForm.getValues()
                setPurchaser(prev => ({ ...prev, ...values }) as any)
            }
        }
    }, [])

    const {
        fields: agents,
        append: appendAgent,
        remove: removeAgent,
    } = useFieldArray({
        control,
        name: 'agents',
    })

    const {
        fields: buyers,
        append: appendBuyer,
        remove: removeBuyer,
    } = useFieldArray({
        control,
        name: 'buyers',
    })

    const agentsWatch = watch('agents')

    useEffect(() => {
        setValue('agents', agentsWatch?.map(assoc('split', 1 / agentsWatch.length)))
    }, [agentsWatch?.map(prop('userId')).join('')])

    // Copy agent's engagementFee to commitmentFee when agent changes
    const prevPrimaryAgentId = useRef<string | undefined>(isNew ? undefined : agentsWatch?.[0]?.userId)

    useEffect(() => {
        const currentAgentId = agentsWatch?.[0]?.userId
        if (!currentAgentId || !usersApi.data) return
        if (currentAgentId !== prevPrimaryAgentId.current) {
            const agent = usersApi.data.find(u => u.userId === currentAgentId)
            if (agent?.engagementFee != null) {
                setValue('commitmentFee', agent.engagementFee)
            }
            prevPrimaryAgentId.current = currentAgentId
        }
    }, [agentsWatch?.[0]?.userId, usersApi.data])

    const authorityStartDate = watch('authorityStartDate')
    const authorityPeriod = watch('authorityPeriod')
    const commissionType = watch('commissionType') ?? 'percentage'

    useEffect(() => {
        setValue('purchaserStatus', purchaser.purchaserStatus, { shouldDirty: false })
    }, [purchaser.purchaserStatus])

    if (Object.keys(formState.errors).length > 0) {
        console.log('errors', formState.errors)
    }

    useEffect(() => {
        console.log('isDirty', formState.isDirty)
        console.log('dirtyFields', formState.dirtyFields)
    }, [formState.isDirty, JSON.stringify(formState.dirtyFields)])

    const onSubmit: SubmitHandler<UpdateClientTabSchema> = async data => {
        setLoading(true)
        try {
            if (isNew) {
                // Include wishlist data from context if available
                let wishlist: WishlistSchema | undefined
                if (purchaser.wishlist && Object.keys(purchaser.wishlist).length > 0) {
                    const firstBuyer = data.buyers[0]
                    wishlist = {
                        ...purchaser.wishlist,
                        firstName: firstBuyer.firstName,
                        lastName: firstBuyer.lastName,
                        email: firstBuyer.email,
                    } as WishlistSchema
                }
                const createdPurchaser = await createPurchaserMutation.mutateAsync({
                    ...data,
                    wishlist,
                } as any)
                const purchaserId = createdPurchaser.purchaserId
                await purchasersApi.refresh()
                setEditMode(false)
                navigate(`../../${purchaserId}`, {
                    relative: 'path',
                    replace: true,
                })
            } else {
                const updatedPurchaser = await updateClientTabMutation.mutateAsync({
                    purchaserId: purchaser.purchaserId,
                    data,
                })
                setPurchaser(updatedPurchaser)
                reset(pick([...clientTabFields, 'buyers'], updatedPurchaser) as any)
                await purchasersApi.refresh()
                invalidateAuditLog()
            }
            showSnack('Client saved', 'success')
        } catch (e) {
            console.error(e)
            showSnack('Error saving client', 'danger')
            throw e
        } finally {
            setLoading(false)
        }
    }

    const handleSubmitPromise = (): Promise<boolean> => {
        return new Promise((resolve, reject) => {
            handleSubmit(
                async data => {
                    try {
                        await onSubmit(data)
                        resolve(true)
                    } catch (error) {
                        resolve(false)
                    }
                },
                error => {
                    resolve(false)
                },
            )()
        })
    }

    const saveForm = async (): Promise<boolean> => {
        if (formDisabled) {
            setLoading(true)
            try {
                const updatedPurchaser = await updateClientTabMutation.mutateAsync({
                    purchaserId: purchaser.purchaserId,
                    data: {
                        agents: purchaser.agents,
                        buyers: purchaser.buyers,
                        purchaserStatus: getValues('purchaserStatus'),
                        additionalDetails: getValues('additionalDetails'),
                    } as any,
                })
                setPurchaser(updatedPurchaser)
                reset(pick([...clientTabFields, 'buyers'], updatedPurchaser) as any)
                purchasersApi.refresh()
                invalidateAuditLog()
                showSnack('Client saved', 'success')
                return true
            } catch (e) {
                console.error(e)
                showSnack('Error saving client', 'danger')
                return false
            } finally {
                setLoading(false)
            }
        } else {
            return await handleSubmitPromise()
        }
    }

    // Expose the `saveForm` method to the parent via ref
    useImperativeHandle(ref, () => ({
        saveForm,
        resetForm: () => reset(pick([...clientTabFields, 'buyers'], purchaser) as any),
    }))

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        //return false
        console.log(formState.dirtyFields)
        console.log('purchaser', purchaser.purchaserId)

        return (
            !isNew &&
            currentLocation.pathname !== nextLocation.pathname &&
            formState.isDirty &&
            Object.keys(formState.dirtyFields).length > 0
        )
    })

    useEffect(() => {
        if (purchaserConfigApi.data) {
            purchaserConfigApi.data.forEach(config =>
                setValue(
                    `additionalDetails.${config.id}`,
                    purchaser.additionalDetails?.[config.id] ?? config.configValues[0].value,
                ),
            )
        }
    }, [purchaserConfigApi.data != undefined])

    if (!purchaserConfigApi.data) {
        return <Loading />
    }

    return (
        <View>
            <FormProvider {...hookForm}>
                <form
                    noValidate
                    //onSubmit={handleSubmit(onSubmit)}
                >
                    <YStack
                        gap="$4"
                        maxWidth={CLIENT_DETAIL_MAX_WIDTH}
                        paddingBottom="$8"
                    >
                        <FormSection title="Buyers">
                            <BuyerList
                                name="Buyer"
                                hideTitle
                                disabled={formDisabled}
                                buyers={buyers}
                                removeBuyer={removeBuyer}
                                buyerType={buyerType}
                                setBuyerType={setBuyerType}
                                extraFields={(buyer, i) =>
                                    buyerType == 'individual' && (
                                        <LocationAutocomplete
                                            name={`buyers.${i}.address`}
                                            control={control}
                                            label="Address"
                                            slotProps={{
                                                input: {
                                                    tabIndex: 0,
                                                },
                                            }}
                                        />
                                    )
                                }
                            />
                            {buyers.length < 6 && !formDisabled && editMode && (
                                <Button
                                    variant="outlined"
                                    width="100%"
                                    borderStyle="dashed"
                                    icon={<AddRounded size={16} />}
                                    onPress={() =>
                                        appendBuyer({
                                            vendorType: buyerType as any,
                                            firstName: '',
                                            lastName: '',
                                            email: '',
                                            mobileNumber: '',
                                            abn: '',
                                        })
                                    }
                                >
                                    Add Buyer
                                </Button>
                            )}
                        </FormSection>
                        <FormSection title="Agent">
                            <Controller
                                name="purchaserStatus"
                                control={control}
                                disabled={formFullyDisabled}
                                render={({ field: { ref, ...field } }) => {
                                    return (
                                        <YStack>
                                            <Label>Status</Label>
                                            <Select
                                                {...field}
                                                onChange={(e: any, value: any) => field.onChange(value)}
                                            >
                                                {purchaserStatusOptions.map(({ value, disabled }) => (
                                                    <Option
                                                        key={value}
                                                        value={value}
                                                        disabled={disabled}
                                                    >
                                                        {capitalize(value)}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </YStack>
                                    )
                                }}
                            />
                            {editMode ? (
                                <Controller
                                    name="agents.0.userId"
                                    control={control}
                                    disabled={formDisabled}
                                    render={field => (
                                        <SlotWrapper
                                            {...field}
                                            label="Agent"
                                        >
                                            <AgentInput
                                                {...field.field}
                                                //filter={propEq('role', 'sales agent')}
                                            />
                                        </SlotWrapper>
                                    )}
                                />
                            ) : (
                                <YStack>
                                    <Label>Agent</Label>
                                    <SizableText>
                                        {agentsWatch?.[0]?.userId ? userName(agentsWatch[0].userId) : ' '}
                                    </SizableText>
                                </YStack>
                            )}
                            {editMode ? (
                                <YStack>
                                    <Label>Second Agent</Label>
                                    <AgentInput
                                        disableClearable={false}
                                        value={agentsWatch?.[1]?.userId ?? null}
                                        disabled={formDisabled || agentsWatch?.[0]?.userId == undefined}
                                        onChange={userId => {
                                            if (userId) {
                                                setValue(`agents.1.userId`, userId)
                                            } else {
                                                removeAgent(1)
                                            }
                                        }}
                                    />
                                </YStack>
                            ) : (
                                <YStack>
                                    <Label>Second Agent</Label>
                                    <SizableText>
                                        {agentsWatch?.[1]?.userId ? userName(agentsWatch[1].userId) : ' '}
                                    </SizableText>
                                </YStack>
                            )}
                        </FormSection>
                        <FormSection title="Pricing">
                            <Controller
                                name="commissionType"
                                disabled={formDisabled}
                                control={control}
                                render={({ field: { ref, ...field } }) => (
                                    <YStack>
                                        <Label>Commission Type</Label>
                                        <Select
                                            {...field}
                                            onChange={(e: any, value: any) => field.onChange(value)}
                                        >
                                            <Option value="percentage">Percentage</Option>
                                            <Option value="flat">Flat Fee</Option>
                                        </Select>
                                    </YStack>
                                )}
                            />
                            <Controller
                                name="commission"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label={
                                            commissionType === 'percentage'
                                                ? 'Commission Rate'
                                                : 'Commission Fee'
                                        }
                                        type="number"
                                        {...field}
                                        startDecorator={commissionType === 'flat' ? '$' : undefined}
                                        endDecorator={commissionType === 'percentage' ? '%' : undefined}
                                        slotProps={{
                                            input: {
                                                step: commissionType === 'percentage' ? 0.1 : 1,
                                            },
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="commitmentFee"
                                disabled={formDisabled}
                                control={control}
                                render={field => (
                                    <SlotInput
                                        label="Commitment Fee"
                                        type="number"
                                        {...field}
                                        startDecorator="$"
                                        slotProps={{
                                            input: {
                                                step: 1,
                                            },
                                        }}
                                    />
                                )}
                            />
                        </FormSection>
                        {authorityStartDate && authorityPeriod && (
                            <span
                                data-tour="authority-filled"
                                hidden
                            />
                        )}
                        <View data-tour="authority-section">
                            <FormSection title="Authority">
                                <Controller
                                    name="authorityStartDate"
                                    disabled={formDisabled}
                                    control={control}
                                    render={field => (
                                        <SlotDatePicker
                                            label="Authority Start Date"
                                            {...field}
                                        />
                                    )}
                                />
                                <Controller
                                    name="authorityPeriod"
                                    control={control}
                                    disabled={formDisabled}
                                    render={field => (
                                        <SlotInput
                                            label="Authority Duration"
                                            type="number"
                                            slotProps={{
                                                input: {
                                                    max: 90,
                                                    min: 1,
                                                },
                                            }}
                                            {...field}
                                            endDecorator="days"
                                        />
                                    )}
                                />
                                <Input
                                    label="Authority End Date"
                                    value={
                                        authorityStartDate && authorityPeriod
                                            ? format(
                                                  addDays(new Date(authorityStartDate), authorityPeriod - 1),
                                                  'd MMM yyyy',
                                              )
                                            : undefined
                                    }
                                    readOnly
                                    disabled
                                />
                            </FormSection>
                        </View>
                        {!isNew && (
                            <>
                                <Card
                                    padding="$4"
                                    gap="$3.5"
                                    data-tour="form6-section"
                                    data-help-id="form6-section"
                                >
                                    <EsignFormStepper
                                        roleNames={[
                                            'Agent',
                                            ...buyers.map((_, i) =>
                                                buyers.length > 1 ? `Buyer ${i + 1}` : 'Buyer',
                                            ),
                                        ]}
                                        title="Form 6"
                                        formType="agentAppointmentForm"
                                        signingService={user.signingService ?? 'annature'}
                                        createDisabled={
                                            buyers.length == 0 ||
                                            purchaser.agents.length == 0 ||
                                            formFullyDisabled
                                        }
                                        validate={async () => {
                                            const start = Date.now()
                                            const requiredValues: Array<keyof UpdateClientTabSchema> = [
                                                'authorityStartDate',
                                                'authorityPeriod',
                                            ]

                                            var shouldFocus = true

                                            const block = await Promise.all(
                                                requiredValues.map(async reqVal => {
                                                    const error = getValues(reqVal) == undefined

                                                    if (error) {
                                                        setError(
                                                            reqVal,
                                                            {
                                                                type: 'required',
                                                                message: 'Required for Form 6',
                                                            },
                                                            {
                                                                shouldFocus,
                                                            },
                                                        )

                                                        shouldFocus = false
                                                        console.log('error', reqVal)
                                                    }
                                                    return error
                                                }),
                                            )
                                            if (block.some(x => x == true)) {
                                                console.log('block time', Date.now() - start)
                                                showSnack(
                                                    'Please fill in the authority dates before creating Form 6',
                                                    'danger',
                                                )
                                                return false
                                            }
                                            return await trigger()
                                        }}
                                        preCreate={async () => {
                                            if (formState.isDirty) {
                                                return await saveForm()
                                                //return true
                                            } else {
                                                return true
                                            }
                                        }}
                                    />
                                </Card>
                            </>
                        )}
                        <View
                            marginTop="$4"
                            marginBottom="$4"
                        >
                            <RenderPurchaserValues
                                tab="purchaser"
                                disabled={formFullyDisabled}
                            />
                        </View>
                    </YStack>
                    <UnsavedModal
                        blocker={blocker}
                        save={(onSuccess, onFail) => saveForm().then(x => (x ? onSuccess() : onFail()))}
                    />
                </form>
            </FormProvider>
        </View>
    )
})

export default CorePurchaserDetails
